import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Tag, Clock, ArrowRight, BookOpen, Share2, Copy, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Separator } from '../components/ui/separator';
import { APP_NAME } from '../config/branding';
import Footer from '../components/Footer';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Helmet } from '@dr.pogodin/react-helmet';
import { trackMetaViewContent } from '../utils/metaPixel';

const CATEGORIES = [
  { name: "Collective Agreements", slug: "collective-agreements" },
  { name: "Contract Interpretation", slug: "contract-interpretation" },
  { name: "Labour Law Basics", slug: "labour-law-basics" },
  { name: "HR & Compliance", slug: "hr-compliance" },
  { name: "Arbitration & Grievances", slug: "arbitration-grievances" },
  { name: "Guides & Explainers", slug: "guides-explainers" },
  { name: "Case Examples", slug: "case-examples" },
];

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  // Add RSS autodiscovery link to head
  useEffect(() => {
    const rssLink = document.createElement('link');
    rssLink.rel = 'alternate';
    rssLink.type = 'application/rss+xml';
    rssLink.title = 'CBAIQ Blog RSS';
    rssLink.href = 'https://cbaiq.com/api/blog/feed.xml';
    rssLink.id = 'rss-autodiscovery';
    
    // Only add if not already present
    if (!document.getElementById('rss-autodiscovery')) {
      document.head.appendChild(rssLink);
    }
    
    return () => {
      const existing = document.getElementById('rss-autodiscovery');
      if (existing) existing.remove();
    };
  }, []);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/blog/posts/${slug}`);
      setPost(response.data);
      trackMetaViewContent(response.data?.title || slug);
      
      // Fetch related posts
      try {
        const relatedResponse = await axios.get(`${API}/blog/related/${slug}?limit=3`);
        setRelatedPosts(relatedResponse.data || []);
      } catch (e) {
        console.error('Failed to fetch related posts:', e);
      }
    } catch (error) {
      console.error('Failed to fetch blog post:', error);
      if (error.response?.status === 404) {
        navigate('/blog', { replace: true });
        toast.error('Article not found');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getReadingTime = (html) => {
    if (!html) return '5 min';
    const text = html.replace(/<[^>]*>/g, '');
    const words = text.split(/\s+/).length;
    const minutes = Math.ceil(words / 200);
    return `${minutes} min read`;
  };

  const handleCopyLink = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error('Failed to copy link');
    }
  };

  const getCategoryName = (slug) => {
    const cat = CATEGORIES.find(c => c.slug === slug);
    return cat?.name || slug;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background noise-bg">
        <header className="glass-header sticky top-0 z-40">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-14">
              <Link to="/" className="font-heading font-semibold text-lg">
                {APP_NAME}
              </Link>
              <nav className="flex items-center gap-4">
                <Link to="/blog" className="text-sm font-medium text-primary">Blog</Link>
              </nav>
            </div>
          </div>
        </header>
        <div className="max-w-3xl mx-auto px-4 py-12">
          <Skeleton className="h-8 w-32 mb-6" />
          <Skeleton className="h-12 w-full mb-4" />
          <Skeleton className="h-6 w-48 mb-8" />
          <Skeleton className="h-64 w-full mb-8 rounded-md" />
          <div className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return null;
  }

  // Get absolute URL for images
  const getAbsoluteImageUrl = (relativeUrl) => {
    if (!relativeUrl) return '';
    if (relativeUrl.startsWith('http')) return relativeUrl;
    // Use the API base URL to construct absolute URL
    const baseUrl = window.location.origin;
    return `${baseUrl}${relativeUrl}`;
  };

  const absoluteImageUrl = getAbsoluteImageUrl(post.og_image_url || post.featured_image_url);
  const pageUrl = `${window.location.origin}/blog/${post.slug}`;
  const pageTitle = post.seo_title || post.title;
  const pageDescription = post.seo_description || post.excerpt || `Read ${post.title} on the ${APP_NAME} Blog`;

  return (
    <div className="min-h-screen bg-background noise-bg">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{pageTitle} | {APP_NAME} Blog</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={absoluteImageUrl || ''} />
        <meta name="twitter:card" content={absoluteImageUrl ? "summary_large_image" : "summary"} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={absoluteImageUrl || ''} />
        <link rel="canonical" href={pageUrl} />
        <link rel="alternate" type="application/rss+xml" title="CBAIQ Blog RSS" href={`${window.location.origin}/api/blog/feed.xml`} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": pageTitle,
          "description": pageDescription,
          "image": absoluteImageUrl || undefined,
          "datePublished": post.published_at || undefined,
          "dateModified": post.updated_at || post.published_at || undefined,
          "mainEntityOfPage": { "@type": "WebPage", "@id": pageUrl },
          "author": { "@type": "Organization", "name": APP_NAME },
          "publisher": { "@type": "Organization", "name": APP_NAME, "url": "https://cbaiq.com" },
        })}</script>
      </Helmet>

      {/* Header */}
      <header className="glass-header sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <Link to="/" className="font-heading font-semibold text-lg hover:text-primary transition-colors">
              {APP_NAME}
            </Link>
            <nav className="flex items-center gap-4">
              <Link to="/blog" className="text-sm font-medium text-primary">Blog</Link>
              <Link to="/pricing">
                <Button variant="ghost" size="sm">Pricing</Button>
              </Link>
              <Link to="/register">
                <Button size="sm" data-testid="post-get-started-btn">Get Started</Button>
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Article */}
      <article className="py-8 sm:py-12 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Breadcrumb */}
          <nav className="mb-6">
            <Link 
              to="/blog" 
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
              data-testid="back-to-blog"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>
          </nav>

          {/* Header */}
          <header className="mb-8">
            {/* Categories */}
            {post.categories?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {post.categories.map((cat) => (
                  <Link key={cat} to={`/blog?category=${cat}`}>
                    <Badge variant="outline" className="hover:bg-primary/10 transition-colors">
                      {getCategoryName(cat)}
                    </Badge>
                  </Link>
                ))}
              </div>
            )}

            {/* Title */}
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight mb-4" data-testid="post-title">
              {post.title}
            </h1>

            {/* Excerpt */}
            {post.excerpt && (
              <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                {post.excerpt}
              </p>
            )}

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {formatDate(post.published_at)}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {getReadingTime(post.content_html)}
              </span>
              <button 
                onClick={handleCopyLink}
                className="flex items-center gap-1 hover:text-primary transition-colors"
                data-testid="share-btn"
              >
                {copied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Share'}
              </button>
            </div>
          </header>

          {/* Featured Image */}
          {post.featured_image_url && (
            <div className="mb-10 rounded-md overflow-hidden border border-border">
              <img 
                src={post.featured_image_url} 
                alt={post.title}
                className="w-full h-auto"
              />
            </div>
          )}

          {/* Content */}
          <div 
            className="prose prose-lg max-w-none
              prose-headings:font-heading prose-headings:font-semibold
              prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
              prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
              prose-p:text-foreground prose-p:leading-relaxed prose-p:mb-4
              prose-a:text-primary prose-a:no-underline hover:prose-a:underline
              prose-strong:text-foreground prose-strong:font-semibold
              prose-ul:my-4 prose-ol:my-4
              prose-li:text-foreground prose-li:my-1
              prose-blockquote:border-l-primary prose-blockquote:bg-secondary/30 prose-blockquote:py-2 prose-blockquote:px-4 prose-blockquote:rounded-r-md
              prose-code:bg-secondary prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:text-sm
              prose-pre:bg-secondary prose-pre:border prose-pre:border-border
              prose-table:border prose-table:border-border
              prose-th:bg-secondary prose-th:px-4 prose-th:py-2 prose-th:text-left
              prose-td:px-4 prose-td:py-2 prose-td:border-t prose-td:border-border
            "
            dangerouslySetInnerHTML={{ __html: post.content_html }}
            data-testid="post-content"
          />

          {/* Tags */}
          {post.tags?.length > 0 && (
            <div className="mt-10 pt-6 border-t border-border">
              <div className="flex flex-wrap items-center gap-2">
                <Tag className="w-4 h-4 text-muted-foreground" />
                {post.tags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="text-xs">
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="mt-12 p-8 bg-card border border-border rounded-md text-center" data-testid="article-cta">
            <BookOpen className="w-10 h-10 text-primary mx-auto mb-4" />
            <h3 className="font-heading text-xl font-semibold mb-2">
              Ask {APP_NAME} about your agreement
            </h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Upload your collective agreement and get instant answers with exact contract citations.
            </p>
            <Link to="/register">
              <Button className="btn-lift" data-testid="article-cta-btn">
                Try {APP_NAME} Free <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </article>

      {/* Related Posts */}
      {relatedPosts.length > 0 && (
        <section className="py-12 px-4 bg-secondary/20 border-t border-border">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-heading text-2xl font-semibold mb-8 text-center">
              Related Articles
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedPosts.map((related) => (
                <Link
                  key={related.id}
                  to={`/blog/${related.slug}`}
                  className="block group"
                  data-testid={`related-${related.slug}`}
                >
                  <article className="bg-card border border-border rounded-md overflow-hidden h-full card-interactive">
                    {related.featured_image_url ? (
                      <div className="h-40 bg-muted">
                        <img 
                          src={related.featured_image_url} 
                          alt={related.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="h-40 bg-gradient-to-br from-secondary to-accent flex items-center justify-center">
                        <BookOpen className="w-8 h-8 text-primary/20" />
                      </div>
                    )}
                    <div className="p-5">
                      <h3 className="font-heading font-semibold mb-2 group-hover:text-primary transition-colors line-clamp-2">
                        {related.title}
                      </h3>
                      {related.excerpt && (
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {related.excerpt}
                        </p>
                      )}
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default BlogPost;
